import { Routes, Route, Navigate } from 'react-router-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { store, persistor } from '@/store'
import { useAuth, useAuthLoading } from '@/store/hooks'
import { useEffect } from 'react'
import { socket } from '@/services/socketService'
import { sessionService } from '@/services/sessionService'
import { LanguageProvider } from '@/contexts/LanguageContext'

// Layout Components
import Layout from '@/components/layout/Layout'
import PublicLayout from '@/components/layout/PublicLayout'

// Public Pages
import HomePage from '@/pages/public/HomePage'
import AboutPage from '@/pages/public/AboutPage'
import ContactPage from '@/pages/public/ContactPage'
import LoginPage from '@/pages/auth/LoginPage'
import UserLoginPage from '@/pages/auth/UserLoginPage'
import DepartmentLoginPage from '@/pages/auth/DepartmentLoginPage'
import AdminControlPage from '@/pages/auth/AdminControlPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import TestPage from '@/pages/test/TestPage'

// Protected Pages
import ComplaintsPage from '@/pages/complaints/ComplaintsPage'
import ComplaintDetailPage from '@/pages/complaints/ComplaintDetailPage'
import NewComplaintPage from '@/pages/complaints/NewComplaintPage'
import UsersPage from '@/pages/admin/UsersPage'
import MembersPage from '@/pages/admin/MembersPage'
import DepartmentsPage from '@/pages/admin/DepartmentsPage'
import CategoriesPage from '@/pages/admin/CategoriesPage'
import EventsPage from '@/pages/events/EventsPage'
import BusinessPage from '@/pages/business/BusinessPage'
import MemberBusinessPage from '@/pages/business/MemberBusinessPage'
import ChargesPage from '@/pages/charges/ChargesPage'
import ReportsPage from '@/pages/reports/ReportsPage'
import ElectionsPage from '@/pages/elections/ElectionsPage'
import MemberElectionsPage from '@/pages/elections/MemberElectionsPage'
import RoleBasedPage from '@/components/RoleBasedPage'
import CreateElectionPage from '@/pages/elections/CreateElectionPage'
import ElectionDetailPage from '@/pages/elections/ElectionDetailPage'
import WhatsAppBotPage from '@/pages/whatsapp/WhatsAppBotPage'
import CMSPage from '@/pages/admin/CMSPage'
import CMSPortalPage from '@/pages/admin/CMSPortalPage'
import EventsManagementPage from '@/pages/admin/cms/EventsManagementPage'
import NotificationsManagementPage from '@/pages/admin/cms/NotificationsManagementPage'
import PhotoGalleryManagementPage from '@/pages/admin/cms/PhotoGalleryManagementPage'
import CommitteeManagementPage from '@/pages/admin/cms/CommitteeManagementPage'
import CommitteeMembersPage from '@/pages/admin/CommitteeMembersPage'
import DepartmentHeadsPage from '@/pages/admin/DepartmentHeadsPage'
import PressReleasesCMS from '@/pages/admin/cms/PressReleasesCMS'
import ProfilePage from '@/pages/profile/ProfilePage'

// Role-based Dashboards
import AdminDashboard from '@/pages/admin/AdminDashboard'
import StaffDashboard from '@/pages/staff/StaffDashboard'
import MemberDashboard from '@/pages/member/MemberDashboard'
// import DepartmentHeadDashboard from '@/pages/department/DepartmentHeadDashboard'
// import WorkerDashboard from '@/pages/worker/WorkerDashboard'
import AnalyticsDashboard from '@/pages/analytics/AnalyticsDashboard'

// Components
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import DynamicDashboard from '@/components/DynamicDashboard'

function AppContent() {
  const { user, isAuthenticated, token } = useAuth()
  const isLoading = useAuthLoading()

  useEffect(() => {
    // Initialize session service
    // sessionService.initializeSession()
  }, [])

  useEffect(() => {
    if (isAuthenticated && user && token) {
      // Connect to socket with user authentication
      // socket.auth = { token }
      // socket.connect()

      // Start session tracking
      if (user.id) {
        const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        sessionService.startSession(sessionId)
      }

      return () => {
        socket.disconnect()
        sessionService.endSession()
      }
    }
  }, [isAuthenticated, user, token])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <Routes>
      {/* Public Routes */}
        <Route path="/" element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="user-login" element={<UserLoginPage />} />
          <Route path="department-login" element={<DepartmentLoginPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="test" element={<TestPage />} />
        </Route>

      {/* Admin Control Route */}
      <Route path="/nfcrwacontrol" element={<AdminControlPage />} />

      {/* Protected Routes */}
      <Route path="/app" element={<ProtectedRoute redirectTo="/nfcrwacontrol"><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DynamicDashboard />} />
        <Route path="complaints" element={<ComplaintsPage />} />
        <Route path="complaints/new" element={<NewComplaintPage />} />
        <Route path="complaints/:id" element={<ComplaintDetailPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="business" element={<RoleBasedPage memberComponent={MemberBusinessPage} adminComponent={BusinessPage} />} />
        <Route path="charges" element={<RoleBasedPage memberComponent={ChargesPage} adminComponent={ChargesPage} />} />
        <Route path="elections" element={<RoleBasedPage memberComponent={MemberElectionsPage} adminComponent={ElectionsPage} />} />
        <Route path="elections/new" element={<CreateElectionPage />} />
        <Route path="elections/:id" element={<ElectionDetailPage />} />
        <Route path="elections/:id/edit" element={<CreateElectionPage />} />
        <Route path="whatsapp-bot" element={<WhatsAppBotPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="analytics" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><AnalyticsDashboard /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="admin/dashboard" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="admin/members" element={<ProtectedRoute roles={['universal_admin']}><MembersPage /></ProtectedRoute>} />
        <Route path="admin/committee-members" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><CommitteeMembersPage /></ProtectedRoute>} />
        <Route path="admin/users" element={<ProtectedRoute roles={['universal_admin']}><UsersPage /></ProtectedRoute>} />
        <Route path="admin/departments" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><DepartmentsPage /></ProtectedRoute>} />
        <Route path="admin/department-heads" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><DepartmentHeadsPage /></ProtectedRoute>} />
        <Route path="admin/categories" element={<ProtectedRoute roles={['universal_admin']}><CategoriesPage /></ProtectedRoute>} />
        <Route path="admin/cms" element={<ProtectedRoute roles={['universal_admin']}><CMSPage /></ProtectedRoute>} />
        <Route path="admin/cms/portal" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><CMSPortalPage /></ProtectedRoute>} />
        <Route path="admin/cms/events" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><EventsManagementPage /></ProtectedRoute>} />
        <Route path="admin/cms/notifications" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><NotificationsManagementPage /></ProtectedRoute>} />
        <Route path="admin/cms/press-releases" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><PressReleasesCMS /></ProtectedRoute>} />
        <Route path="admin/cms/business" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><BusinessPage /></ProtectedRoute>} />
        <Route path="admin/cms/committee" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><CommitteeManagementPage /></ProtectedRoute>} />
        <Route path="admin/cms/gallery" element={<ProtectedRoute roles={['universal_admin', 'office_admin']}><PhotoGalleryManagementPage /></ProtectedRoute>} />
        
        {/* Staff Routes */}
        <Route path="staff/dashboard" element={<ProtectedRoute roles={['department_head', 'front_worker', 'staff', 'committee_member']}><StaffDashboard /></ProtectedRoute>} />
        
        {/* Member Routes */}
        <Route path="member/dashboard" element={<ProtectedRoute roles={['member']}><MemberDashboard /></ProtectedRoute>} />
        
        <Route path="reports" element={<ProtectedRoute roles={['universal_admin', 'office_admin', 'department_head', 'front_worker']}><ReportsPage /></ProtectedRoute>} />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={<LoadingSpinner size="lg" />} persistor={persistor}>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </PersistGate>
    </Provider>
  )
}

export default App
